import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import Svg, { Defs, LinearGradient, Path, Stop } from 'react-native-svg';

import { useSvgId } from '../../../../utils/hooks/useSvgId';

/**
 * The nav plate, Figma "Nav Base" (3618:68767) - the bar and the Home notch unioned into one
 * shape, drawn as a black backdrop, a red gradient face and a gradient hairline.
 *
 * It is inlined rather than imported from `nav-base.svg` because that file's gradient ids are
 * baked in. On web every screen in the stack stays mounted, so two footers can sit in the DOM at
 * once - Store and Ranking bring no chrome of their own, which leaves two blurred MainLayouts
 * both drawing one. `url(#paint0_linear_2324_664)` then resolves to whichever copy comes first in
 * document order, the fills silently fail, and the bar paints as the bare black path. Suffixing
 * the ids per instance with `useSvgId` is the same fix the rest of the app's SVGs use.
 */
type Props = {
	width: number;
	height: number;
	style?: StyleProp<ViewStyle>;
};

export const NavBasePlate = ({ width, height, style }: Props) => {
	const grad0 = useSvgId('navBaseGrad0');
	const grad1 = useSvgId('navBaseGrad1');

	return (
		<Svg
			width={width}
			height={height}
			viewBox='0 0 371 87'
			fill='none'
			style={style}
			pointerEvents='none'
		>
			<Defs>
				<LinearGradient
					id={grad0}
					x1='185.5'
					y1='1.5'
					x2='185.5'
					y2='77.5'
					gradientUnits='userSpaceOnUse'
				>
					<Stop offset='0' stopColor='#770700' />
					<Stop offset='0.80127' stopColor='#320400' />
				</LinearGradient>
				<LinearGradient
					id={grad1}
					x1='185.5'
					y1='77.5'
					x2='185.5'
					y2='1.5'
					gradientUnits='userSpaceOnUse'
				>
					<Stop offset='0' stopColor='#DA600E' />
					<Stop offset='1' stopColor='#F46A0C' />
				</LinearGradient>
			</Defs>
			<Path
				d='M185.5 1.5C195.259 1.5 204.005 5.85151 209.92 12.7266H212.597C214.974 12.7266 217.201 13.3728 219.114 14.499H352.425C359.646 14.499 365.5 20.4476 365.5 27.7852V63.2129C365.5 70.5504 359.646 76.499 352.425 76.499H18.5752C11.354 76.499 5.5 70.5504 5.5 63.2129V27.7852C5.5 20.4476 11.354 14.499 18.5752 14.499H151.886C153.799 13.3728 156.026 12.7266 158.403 12.7266H161.08C166.995 5.85151 175.741 1.5 185.5 1.5Z'
				fill='black'
			/>
			<Path
				d='M185.5 1.5C195.259 1.5 204.005 5.85151 209.92 12.7266H212.597C214.974 12.7266 217.201 13.3728 219.114 14.499H352.425C359.646 14.499 365.5 20.4476 365.5 27.7852V63.2129C365.5 70.5504 359.646 76.499 352.425 76.499H18.5752C11.354 76.499 5.5 70.5504 5.5 63.2129V27.7852C5.5 20.4476 11.354 14.499 18.5752 14.499H151.886C153.799 13.3728 156.026 12.7266 158.403 12.7266H161.08C166.995 5.85151 175.741 1.5 185.5 1.5Z'
				fill={`url(#${grad0})`}
			/>
			<Path
				d='M209.92 12.7266L209.541 13.0527L209.691 13.2266H209.92V12.7266ZM219.114 14.499L218.861 14.9299L218.978 14.999H219.114V14.499ZM217.578 76.499V75.999H217.477L217.384 76.0381L217.578 76.499ZM153.422 76.499L153.616 76.0381L153.523 75.999H153.422V76.499ZM151.886 14.499V14.999H152.022L152.139 14.9299L151.886 14.499ZM161.08 12.7266V13.2266H161.309L161.459 13.0527L161.08 12.7266ZM185.5 1.5V2C195.107 2 203.717 6.28298 209.541 13.0527L209.92 12.7266L210.299 12.4005C204.293 5.42004 195.411 1 185.5 1V1.5ZM209.92 12.7266V13.2266H212.597V12.7266V12.2266H209.92V12.7266ZM212.597 12.7266V13.2266C214.883 13.2266 217.022 13.8476 218.861 14.9299L219.114 14.499L219.368 14.0682C217.381 12.898 215.066 12.2266 212.597 12.2266V12.7266ZM219.114 14.499V14.999H352.425V14.499V13.999H219.114V14.499ZM352.425 14.499V14.999C359.362 14.999 365 20.7162 365 27.7852H365.5H366C366 20.1791 359.93 13.999 352.425 13.999V14.499ZM365.5 27.7852H365V63.2129H365.5H366V27.7852H365.5ZM365.5 63.2129H365C365 70.2818 359.362 75.999 352.425 75.999V76.499V76.999C359.93 76.999 366 70.819 366 63.2129H365.5ZM352.425 76.499V75.999H18.5752V76.499V76.999H352.425V76.499ZM18.5752 76.499V75.999C11.6377 75.999 6 70.2818 6 63.2129H5.5H5C5 70.819 11.0704 76.999 18.5752 76.999V76.499ZM5.5 63.2129H6V27.7852H5.5H5V63.2129H5.5ZM5.5 27.7852H6C6 20.7162 11.6377 14.999 18.5752 14.999V14.499V13.999C11.0704 13.999 5 20.1791 5 27.7852H5.5ZM18.5752 14.499V14.999H151.886V14.499V13.999H18.5752V14.499ZM151.886 14.499L152.139 14.9299C153.978 13.8476 156.117 13.2266 158.403 13.2266V12.7266V12.2266C155.934 12.2266 153.619 12.898 151.632 14.0682L151.886 14.499ZM158.403 12.7266V13.2266H161.08V12.7266V12.2266H158.403V12.7266ZM161.08 12.7266L161.459 13.0527C167.283 6.28298 175.893 2 185.5 2V1.5V1C175.589 1 166.707 5.42004 160.701 12.4005L161.08 12.7266Z'
				fill={`url(#${grad1})`}
			/>
		</Svg>
	);
};

export default NavBasePlate;
